// src/hooks/useMetaTags.ts

interface OpenGraphTags {
  title?: string;
  description?: string;
  url?: string;
  type?: string;
  site_name?: string;
  image?: string;
  imageWidth?: string;
  imageHeight?: string;
}

interface MetaTagsConfig {
  title: string;
  description: string;
  canonical: string;
  robots?: string;
  og?: OpenGraphTags;
}

export const setMetaTags = ({
  title,
  description,
  canonical,
  robots = "index, follow",
  og = {},
}: MetaTagsConfig): void => {
  const setMeta = (
    attr: "name" | "property",
    name: string,
    content: string
  ): void => {
    let element = document.querySelector<HTMLMetaElement>(
      `meta[${attr}="${name}"]`
    );

    if (!element) {
      element = document.createElement("meta");
      element.setAttribute(attr, name);
      document.head.appendChild(element);
    }

    element.setAttribute("content", content);
  };

  // Set <title>
  document.title = title;

  // Set standard meta tags
  setMeta("name", "description", description);
  setMeta("name", "robots", robots);

  // Set canonical link
  let canonicalTag = document.querySelector<HTMLLinkElement>(
    "link[rel='canonical']"
  );

  if (!canonicalTag) {
    canonicalTag = document.createElement("link");
    canonicalTag.setAttribute("rel", "canonical");
    document.head.appendChild(canonicalTag);
  }

  canonicalTag.setAttribute("href", canonical);

  // Set Open Graph tags
  if (og.title) setMeta("property", "og:title", og.title);
  if (og.description) setMeta("property", "og:description", og.description);
  if (og.url) setMeta("property", "og:url", og.url);
  if (og.type) setMeta("property", "og:type", og.type);
  if (og.site_name) setMeta("property", "og:site_name", og.site_name);
  if (og.image) setMeta("property", "og:image", og.image);
  if (og.imageWidth) setMeta("property", "og:image:width", og.imageWidth);
  if (og.imageHeight) setMeta("property", "og:image:height", og.imageHeight);
};


export const clearMetaTags = () => {
  const propertiesToClear = [
    "description",
    "robots",
    "og:title",
    "og:description",
    "og:url",
    "og:type",
    "og:site_name",
    "og:image",
    "og:image:width",
    "og:image:height",
  ];

  propertiesToClear.forEach((prop) => {
    const attr = prop.startsWith("og:") ? "property" : "name";
    const selector = `meta[${attr}="${prop}"]`;
    const element = document.head.querySelector(selector);
    if (element) {
      element.remove();
    }
  });

  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.remove();
};
